import { useMemo } from 'react';

import { AutocompleteV2, Typography, Stack, tokens } from '@cast/design-system';

import { useClmCompatibleFamiliesPicker } from './ClmCompatibleFamiliesPickerProvider';
import { InstanceChip } from './InstanceChip';
import {
  ALL_INSTANCES_BY_PROVIDER,
  INSTANCE_COMPATIBILITY_MAPS,
  UNMIGRATABLE_INSTANCES_BY_PROVIDER,
} from './InstanceCompatibilityMap';
import { getCompatibleCount } from './utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type InstanceItem = {
  family: string;
  nonMigratable: boolean;
};

export const BaseInstanceSelector = () => {
  const { cluster } = useCluster();
  const { availableInstances, baseInstance, setBaseInstance } =
    useClmCompatibleFamiliesPicker();

  const baseInstances = useMemo(() => {
    const allInstances =
      ALL_INSTANCES_BY_PROVIDER[cluster.providerType] ?? new Set<string>();
    return availableInstances.filter((instance) => allInstances.has(instance));
  }, [availableInstances, cluster.providerType]);

  const nonMigratableInstances = useMemo(() => {
    const nonMigratableInstances = new Set(
      UNMIGRATABLE_INSTANCES_BY_PROVIDER[cluster.providerType] ?? []
    );
    return availableInstances.filter((instance) =>
      nonMigratableInstances.has(instance)
    );
  }, [availableInstances, cluster.providerType]);

  const allInstances = useMemo<InstanceItem[]>(
    () => [
      ...baseInstances.map((family) => ({ family, nonMigratable: false })),
      ...nonMigratableInstances.map((family) => ({
        family,
        nonMigratable: true,
      })),
    ],
    [baseInstances, nonMigratableInstances]
  );

  return (
    <Stack gap={4} data-testid="base-instance-selector">
      <Typography variant="P12M">Select a starting instance family</Typography>
      <Typography
        variant="P12R"
        color={tokens.theme.fg.structural.secondary}
        pb={12}
      >
        Select a base instance family, or search to explore all compatible
        choices.
      </Typography>
      {allInstances.length <= 10 ? (
        <Stack gap={8}>
          <Stack direction="row" gap={8} flexWrap="wrap">
            {baseInstances.map((instance) => {
              const checked = instance === baseInstance;
              return (
                <InstanceChip
                  key={instance}
                  instance={instance}
                  checked={checked}
                  onClick={() =>
                    setBaseInstance(checked ? undefined : instance)
                  }
                />
              );
            })}
          </Stack>
          {nonMigratableInstances.length > 0 && (
            <Stack gap={4}>
              <Typography
                variant="A2"
                color={tokens.theme.fg.structural.secondary}
              >
                Not eligible for live migration: CPU generation mismatch
              </Typography>
              <Stack direction="row" gap={8} flexWrap="wrap">
                {nonMigratableInstances.map((instance) => (
                  <InstanceChip
                    key={instance}
                    instance={instance}
                    checked={false}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      ) : (
        <AutocompleteV2
          popoverSize={{
            maxWidth: 523,
          }}
          size="small"
          items={allInstances}
          value={baseInstance ?? null}
          getStringValue={(instance) => instance.family}
          getLabel={(instance) => instance.family}
          groupBy={(instance) =>
            instance.nonMigratable
              ? 'Not eligible for live migration: CPU generation mismatch'
              : 'Eligible for live migration'
          }
          onChange={({ value }) =>
            setBaseInstance(
              value === baseInstance ? undefined : value ?? undefined
            )
          }
          isItemDisabled={(instance) => instance.nonMigratable}
          testId="base-instance-select"
          renderMenuItem={({ item }) => {
            if (item.nonMigratable) {
              return <InstanceChip instance={item.family} checked={false} />;
            }
            const compatibilityMap =
              INSTANCE_COMPATIBILITY_MAPS[cluster.providerType];
            const count = compatibilityMap
              ? getCompatibleCount(
                  item.family,
                  compatibilityMap,
                  availableInstances
                )
              : 0;
            return (
              <Stack
                direction="row"
                justifyContent="space-between"
                width="100%"
              >
                <InstanceChip instance={item.family} checked />
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  Compatible:{' '}
                  <Typography
                    component="span"
                    variant="P12M"
                    color={tokens.theme.fg.structural.primary}
                  >
                    {count}
                  </Typography>
                </Typography>
              </Stack>
            );
          }}
        />
      )}
    </Stack>
  );
};
